import React from 'react';
import { isTerritoryLayer } from '../../utils/territoryLayers';

const KIND_LABELS = {
  wmu: 'Wildlife Management Units (WMU)',
  bmu: 'Bear Management Units (BMU)',
  fmu: 'Forest Management Units (FMU)',
  fmz: 'Fisheries Management Zones (FMZ)',
  trapline: 'Trapline Areas',
  lakes: 'Lakes and AOC',
};

export function isLakesLayer(layer) {
  return ['lakes', 'lakes_and_aoc'].includes(layer.layerId);
}

const TRAPPED_SPECIES = /(^|[^a-z])(beaver|muskrat|marten|fisher|otter|mink|lynx|bobcat|fox|wolf|wolves|coyote|weasel|ermine|raccoon|skunk|wolverine|squirrel|trapp\w*)([^a-z]|$)/i;
const BEAR_SPECIES = /(^|[^a-z])bears?([^a-z]|$)/i;

function hasToken(value, token) {
  return new RegExp(`(^|[^a-z])${token}([^a-z]|$)`).test(value);
}

function managementKind(layer) {
  if (isLakesLayer(layer)) return 'lakes';
  const value = `${layer.layerId || ''} ${layer.layerType || ''} ${layer.displayName || ''}`.toLowerCase();
  if (hasToken(value, 'wmu') || value.includes('wildlife management')) return 'wmu';
  if (hasToken(value, 'bmu') || value.includes('bear management')) return 'bmu';
  if (hasToken(value, 'fmu') || value.includes('forest management')) return 'fmu';
  if (hasToken(value, 'fmz') || value.includes('fisheries management')) return 'fmz';
  if (value.includes('trapline') || value.includes('trapping') || hasToken(value, 'trap')) return 'trapline';
  return 'other';
}

const TREE_SPECIES = /(^|[^a-z])(tree|spruce|pine|birch|maple|cedar|poplar|aspen|oak|fir|larch|tamarack|hemlock|basswood|elm|ash|alder|willow|cherry|balsam|beech|hickory|walnut|sumac|dogwood)s?([^a-z]|$)/i;

function ruleMatches(rule, category, speciesName) {
  const name = (speciesName || '').toLowerCase();
  if ((rule.categories || []).includes(category)) return true;
  return (rule.keywords || []).some((word) => word && name.includes(String(word).toLowerCase()));
}

export function configuredManagementLayers(layers, category, speciesName = '', rules = []) {
  const ids = new Set(rules.filter((rule) => ruleMatches(rule, category, speciesName))
    .flatMap((rule) => rule.layerIds || []));
  return layers.filter((layer) => !isTerritoryLayer(layer) && ids.has(layer.layerId));
}

export function managementKindsFor(category, speciesName = '') {
  const kinds = new Set();
  const isBear = BEAR_SPECIES.test(speciesName || '');
  if (['hunting', 'birds'].includes(category)) kinds.add(isBear && category === 'hunting' ? 'bmu' : 'wmu');
  if (isBear && !kinds.size) kinds.add('bmu');
  if (category === 'fishing') { kinds.add('fmz'); kinds.add('lakes'); }
  if (category === 'plants_medicine' || TREE_SPECIES.test(speciesName || '')) kinds.add('fmu');
  if (category === 'trapping' || TRAPPED_SPECIES.test(speciesName || '')) kinds.add('trapline');
  return kinds;
}

export function relevantManagementLayers(layers, category, speciesName = '', rules = []) {
  const wanted = managementKindsFor(category, speciesName);
  const matchedRules = (rules || []).filter((rule) => ruleMatches(rule, category, speciesName));
  const ruleLayerIds = new Set(matchedRules.flatMap((rule) => rule.layerIds || []));
  const assignedAnywhere = new Set((rules || []).flatMap((rule) => rule.layerIds || []));
  return (layers || []).filter((layer) => {
    if (ruleLayerIds.has(layer.layerId)) return true;
    // A layer that setup assigned to specific items only appears for those items.
    if (assignedAnywhere.has(layer.layerId)) return false;
    const kind = managementKind(layer);
    return kind === 'other' || wanted.has(kind);
  });
}

function LayerToggle({ active, disabled = false, label, onClick, tone = '' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={`harvest-map-flow__layers-pill ${tone}${!disabled && active ? ' active' : ''}`}
    >
      {label}
    </button>
  );
}

export function GuidanceLayerControls({
  selectedSpeciesId, showStewardship, onToggleStewardship, showRestrictions, onToggleRestrictions,
}) {
  return <>
    <LayerToggle active={showStewardship} disabled={!selectedSpeciesId} label="Stewardship guidance" onClick={onToggleStewardship} tone="stewardship" />
    <LayerToggle active={showRestrictions} disabled={!selectedSpeciesId} label="Caution / restrictions" onClick={onToggleRestrictions} tone="restrictions" />
  </>;
}

export default function MapLayerControls({
  guidanceOnly = false,
  selectedSpeciesId = '',
  selectedSpeciesName = '',
  selectedCategory = '',
  layerRules = [],
  showTraditionalLands = false,
  onToggleTraditionalLands,
  traditionalLandsEmpty = false,
  managedLayers = [],
  activeManagedLayerIds = [],
  onToggleManagedLayer,
  managedLayersLoading = false,
}) {
  const speciesLayersDisabled = !selectedSpeciesId;
  const relevantLayers = guidanceOnly
    ? configuredManagementLayers(managedLayers, selectedCategory, selectedSpeciesName, layerRules)
    : relevantManagementLayers(managedLayers, selectedCategory, selectedSpeciesName, layerRules);
  const activeIds = new Set(activeManagedLayerIds.map(String));
  const missingKinds = [...managementKindsFor(selectedCategory, selectedSpeciesName)]
    .filter((kind) => !relevantLayers.some((layer) => managementKind(layer) === kind));
  const unavailableIds = [...new Set(layerRules.filter((rule) => ruleMatches(rule, selectedCategory, selectedSpeciesName))
    .flatMap((rule) => rule.layerIds || []))]
    .filter((id) => !managedLayers.some((layer) => layer.layerId === id));

  return (
    <div className="harvest-map-flow__layers-wrap">
      <div className="harvest-map-flow__layers-heading">
        <strong>Relevant map layers</strong>
        <span>{selectedSpeciesName || 'Choose a species or resource'}</span>
      </div>
      <div className="harvest-map-flow__layers">
        <span className="harvest-map-flow__layers-hint">Published territory boundaries are shown by default. Use Hide territory to hide them independently of species and category rules.</span>
        {!guidanceOnly && <LayerToggle active={showTraditionalLands} label="RSMIN Traditional Lands" onClick={onToggleTraditionalLands} tone="traditional" />}

        {relevantLayers.map((layer) => (
          <LayerToggle
            key={layer._id}
            active={activeIds.has(String(layer._id))}
            label={layer.displayName}
            onClick={() => onToggleManagedLayer(layer)}
            tone="managed"
          />
        ))}

        {managedLayersLoading && <span className="harvest-map-flow__layers-hint">Loading reference layers...</span>}
        {!managedLayersLoading && unavailableIds.length > 0 && (
          <span className="harvest-map-flow__layers-hint">Configured layers unavailable: {unavailableIds.join(', ')}. Staff must check the active version, publication audience and Harvesting module availability.</span>
        )}
        {!guidanceOnly && !managedLayersLoading && missingKinds.map((kind) => (
          <LayerToggle key={kind} disabled label={`${KIND_LABELS[kind]} — unavailable`} />
        ))}
        {speciesLayersDisabled && <span className="harvest-map-flow__layers-hint">Published species-specific guidance requires a recognized species.</span>}
        {!guidanceOnly && showTraditionalLands && traditionalLandsEmpty && (
          <span className="harvest-map-flow__layers-hint">No published traditional lands yet — a community admin can approve them in Community Admin → Traditional Lands.</span>
        )}
        {relevantLayers.some((layer) => isLakesLayer(layer) && activeIds.has(String(layer._id))) && (
          <span className="harvest-map-flow__layers-hint">Lakes and AOC: zoom in to a local area (zoom 11+) to see lake shapes.</span>
        )}
      </div>
    </div>
  );
}
